import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Shield } from 'react-feather';
import { toast } from 'react-toastify';
import { getSupportHistoryAPI, startSupportSessionAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';

const SUPPORT_KEY = 'mh_support_session';

const DURATIONS = [
  { value: '30', label: '30 minutes', ms: 30 * 60 * 1000 },
  { value: '60', label: '1 hour', ms: 60 * 60 * 1000 },
  { value: '120', label: '2 hours', ms: 120 * 60 * 1000 },
  { value: 'custom', label: 'Custom', ms: 60 * 60 * 1000 },
];

export default function SupportMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('60');
  const [piiMask, setPiiMask] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [customMin, setCustomMin] = useState(60);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSupportHistoryAPI(id);
      setHistory(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const startSession = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (!confirmed) {
      toast.error('Please confirm audit logging');
      return;
    }
    const d = DURATIONS.find((x) => x.value === duration) || DURATIONS[1];
    const ms = duration === 'custom' ? customMin * 60 * 1000 : d.ms;
    try {
      await startSupportSessionAPI(id, {
        reason: reason.trim(),
        durationKey: duration,
        piiMasked: piiMask,
      });
      const until = Date.now() + ms;
      sessionStorage.setItem(SUPPORT_KEY, JSON.stringify({ tenantId: id, until }));
      toast.success('Support session started');
      navigate(platformUrl(`tenants/${id}`));
    } catch (e) {
      toast.error(e.message || 'Failed to start');
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Support session"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Tenants', path: platformUrl('tenants') },
            { label: id, path: platformUrl(`tenants/${id}`) },
            { label: 'Support' },
          ]}
        />
        <LoadingSkeleton rows={4} cols={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Support session"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Tenants', path: platformUrl('tenants') },
            { label: id, path: platformUrl(`tenants/${id}`) },
            { label: 'Support' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load history"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Support impersonation"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Tenants', path: platformUrl('tenants') },
          { label: id, path: platformUrl(`tenants/${id}`) },
          { label: 'Support' },
        ]}
      />

      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="h6 d-flex align-items-center gap-2">
            <Shield size={18} /> Policy
          </Card.Title>
          <Form.Group className="mb-3">
            <Form.Label>Reason *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe why you need access…"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Duration</Form.Label>
            <div className="d-flex flex-column gap-2">
              {DURATIONS.map((d) => (
                <Form.Check
                  key={d.value}
                  type="radio"
                  name="dur"
                  label={d.label}
                  checked={duration === d.value}
                  onChange={() => setDuration(d.value)}
                />
              ))}
            </div>
            {duration === 'custom' && (
              <Form.Control
                type="number"
                className="mt-2"
                min={15}
                value={customMin}
                onChange={(e) => setCustomMin(parseInt(e.target.value, 10) || 15)}
                placeholder="Minutes"
              />
            )}
          </Form.Group>
          <Form.Check
            className="mb-3"
            label="PII masking ON (recommended)"
            checked={piiMask}
            onChange={(e) => setPiiMask(e.target.checked)}
          />
          <Form.Check
            label="I confirm this access is logged and audited"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <Button
            className="mt-3"
            variant="warning"
            disabled={!reason.trim() || !confirmed}
            onClick={startSession}
          >
            Start Support Session
          </Button>
        </Card.Body>
      </Card>

      <h6 className="mb-2">Previous sessions</h6>
      {history.length === 0 ? (
        <EmptyState icon={Shield} title="No previous sessions" description="History will appear here after sessions end." />
      ) : (
        <Table responsive size="sm" className="bg-white rounded border">
          <thead>
            <tr>
              <th>Date</th>
              <th>Admin</th>
              <th>Reason</th>
              <th>Duration</th>
              <th>PII masked</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td>{new Date(h.date).toLocaleString()}</td>
                <td>{h.admin}</td>
                <td>{h.reason}</td>
                <td>{h.durationMin}m</td>
                <td>{h.piiMasked ? 'Yes' : 'No'}</td>
                <td>{h.outcome}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
